import { useCallback, useMemo } from 'react';
import { isAdminOnlyDoc } from '../../../../../../../../utils/docTags';

/**
 * Derived doc metadata and filtered lists for the attached-docs UI.
 */
export function useAttachedDocsData({
  task,
  allDocs,
  libraryDocs,
  attachQuery,
  canViewAdminDocs,
}) {
  const docsById = useMemo(() => {
    const map = new Map();
    for (const d of allDocs) map.set(d.id, d);
    return map;
  }, [allDocs]);

  const attachedDocs = useMemo(
    () => (Array.isArray(task?.attachedDocs) ? task.attachedDocs : []),
    [task?.attachedDocs]
  );

  const resolveDocMeta = useCallback((docId) => {
    const live = docsById.get(docId);
    if (live) return live;
    const entry = attachedDocs.find((e) => e.docId === docId);
    if (!entry) return undefined;
    return { id: docId, title: entry.title, icon: entry.icon };
  }, [docsById, attachedDocs]);

  const visibleAttached = useMemo(
    () =>
      attachedDocs.filter((entry) => {
        const meta = docsById.get(entry.docId);
        if (!meta) return true;
        return canViewAdminDocs || !isAdminOnlyDoc(meta);
      }),
    [attachedDocs, docsById, canViewAdminDocs]
  );

  const attachableDocs = useMemo(() => {
    const attachedIds = new Set(attachedDocs.map((e) => e.docId));
    const q = attachQuery.trim().toLowerCase();
    return libraryDocs.filter((doc) => {
      if (doc.deletedAt) return false;
      if (attachedIds.has(doc.id)) return false;
      if (!canViewAdminDocs && isAdminOnlyDoc(doc)) return false;
      if (q) {
        const haystack = `${doc.title} ${(doc.tags || []).join(' ')}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [libraryDocs, attachedDocs, attachQuery, canViewAdminDocs]);

  return {
    docsById,
    resolveDocMeta,
    attachedDocs,
    visibleAttached,
    attachableDocs,
  };
}
